import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';
import { getPois } from '../services/poiService';
import { snapToNavmesh } from '../services/navmeshService';
import { logEvent } from '../services/debugLog';
import { isSafeHttpUrl } from '../utils/html';
import type { PoseTransformService } from '../services/poseTransformService';

/**
 * AR display layer for POI media attachments — image panels, video panels,
 * and 3D objects anchored at their POI positions in map space. Content is
 * always on display once localized (independent of navigation); the scan
 * occluder hides it behind real walls/floors.
 *
 * Mobile-performance choices:
 * - Panels billboard around Y toward the camera (cheap per-frame yaw).
 * - Videos are muted/looped (autoplay-safe) and only PLAY within
 *   VIDEO_PLAY_DIST_M — decoders are expensive; far videos pause.
 * - GLB models are auto-scaled to MODEL_MAX_DIM_M so an oversized upload
 *   can't fill the room.
 */

const IMAGE_MAX_DIM_M = 0.9;
const VIDEO_WIDTH_M = 1.0;
const MODEL_MAX_DIM_M = 0.8;
/** Panels float at roughly eye level above the POI's floor point. */
const DISPLAY_HEIGHT_M = 1.5;
const VIDEO_PLAY_DIST_M = 8;
/** Audio narration triggers on approach, re-arms after leaving. */
const AUDIO_TRIGGER_DIST_M = 4;
const AUDIO_RESET_DIST_M = 6;

export class MediaLayer {
  private group = new THREE.Group();
  private billboards: THREE.Object3D[] = [];
  private videos: Array<{
    el: HTMLVideoElement;
    obj: THREE.Mesh;
    muteBtn: THREE.Mesh;
    /** Explicit user pause — distance gating won't auto-resume it. */
    userPaused: boolean;
  }> = [];
  private links: Array<{ obj: THREE.Object3D; url: string; name: string }> = [];
  private audios: Array<{
    el: HTMLAudioElement;
    icon: THREE.Mesh;
    name: string;
    inRange: boolean;
  }> = [];
  private raycaster = new THREE.Raycaster();
  private loadStarted = false;

  /** First-time proximity (<8 m) per media POI per session → analytics. */
  private impressionTargets: Array<{ poiId: string; obj: THREE.Object3D }> = [];
  private impressed = new Set<string>();

  constructor(
    private poseTransform: PoseTransformService,
    /** Called when a link button is tapped — e.g. open an in-AR overlay. Falls back to a new tab when not provided. */
    private onOpenLink?: (url: string, name: string) => void,
    /** Called once per POI per session when its media first comes near. */
    private onImpression?: (poiId: string) => void,
  ) {
    this.group.name = 'poi-media-layer';
    this.poseTransform.mapRoot.add(this.group);
  }

  /** Loads all media-bearing POIs once (call after first localization). */
  async load(): Promise<void> {
    if (this.loadStarted) return;
    this.loadStarted = true;

    const now = Date.now();
    const pois = (await getPois()).filter((p) => {
      if (!p.mediaType || !p.mediaUrl) return false;
      // Campaign window: media outside its scheduled [start, end] stays off.
      if (p.mediaStart && Date.parse(p.mediaStart) > now) {
        logEvent('session', `media "${p.name}" not started yet (campaign)`);
        return false;
      }
      if (p.mediaEnd && Date.parse(p.mediaEnd) < now) {
        logEvent('session', `media "${p.name}" campaign ended`);
        return false;
      }
      return true;
    });
    for (const poi of pois) {
      try {
        const before = this.group.children.length;
        // Anchor media to the walkable FLOOR under the POI, not the POI's raw
        // height — POIs marked at wall/eye height (e.g. y=1.95) would push
        // panels (+1.5 m) into the ceiling, where the occluder hides them.
        // The snap probe is biased downward so a POI at chest height snaps to
        // its own floor, not the storey above.
        const probe = { x: poi.position.x, y: poi.position.y - 1.2, z: poi.position.z };
        const floorPt = snapToNavmesh(probe);
        const base = floorPt
          ? { x: poi.position.x, y: floorPt.y, z: poi.position.z }
          : poi.position;
        if (floorPt && Math.abs(floorPt.y - poi.position.y) > 0.3) {
          logEvent('session', `media "${poi.name}" floor-anchored y ${poi.position.y.toFixed(2)} → ${floorPt.y.toFixed(2)}`);
        }

        if (poi.mediaType === 'image') await this.addImage(poi.mediaUrl!, base);
        else if (poi.mediaType === 'video') this.addVideo(poi.mediaUrl!, base);
        else if (poi.mediaType === 'model') await this.addModel(poi.mediaUrl!, base);
        else if (poi.mediaType === 'link') this.addLink(poi.mediaUrl!, poi.name, base);
        else if (poi.mediaType === 'audio') this.addAudio(poi.mediaUrl!, poi.name, base);
        // Whatever root object the add* created is this POI's impression anchor.
        const added = this.group.children[before];
        if (added) this.impressionTargets.push({ poiId: poi.id, obj: added });
      } catch (err) {
        logEvent('error', `media failed for "${poi.name}": ${err instanceof Error ? err.message : String(err)}`);
      }
    }
    if (pois.length > 0) logEvent('session', `media layer ready: ${pois.length} object(s)`);
  }

  private async addImage(url: string, pos: { x: number; y: number; z: number }): Promise<void> {
    const tex = await new THREE.TextureLoader().loadAsync(url);
    const img = tex.image as { width: number; height: number };
    const aspect = img.width / img.height;
    const w = aspect >= 1 ? IMAGE_MAX_DIM_M : IMAGE_MAX_DIM_M * aspect;
    const h = aspect >= 1 ? IMAGE_MAX_DIM_M / aspect : IMAGE_MAX_DIM_M;
    const panel = new THREE.Mesh(
      new THREE.PlaneGeometry(w, h),
      new THREE.MeshBasicMaterial({ map: tex, side: THREE.DoubleSide }),
    );
    panel.position.set(pos.x, pos.y + DISPLAY_HEIGHT_M, pos.z);
    this.group.add(panel);
    this.billboards.push(panel);
  }

  private addVideo(url: string, pos: { x: number; y: number; z: number }): void {
    const video = document.createElement('video');
    // ORDER MATTERS: crossOrigin must be set BEFORE src, otherwise the fetch
    // is CORS-tainted and WebGL rejects the texture upload (invisible panel).
    video.crossOrigin = 'anonymous';
    video.muted = true; // required for autoplay on mobile
    video.loop = true;
    video.playsInline = true;
    video.setAttribute('playsinline', ''); // iOS honors the attribute, not just the property
    video.preload = 'auto';
    video.src = url;
    video.addEventListener('loadedmetadata', () =>
      logEvent('session', `video ready ${video.videoWidth}x${video.videoHeight}`),
    );
    video.addEventListener('error', () =>
      logEvent('error', `video element failed: ${video.error?.message ?? 'unknown'} (${url.slice(-40)})`),
    );
    // iOS quirk: some Safari versions won't decode video for detached
    // elements — keep it in the DOM, invisibly (display:none would stop decode).
    video.style.cssText = 'position:fixed;width:1px;height:1px;opacity:0;pointer-events:none;';
    document.body.appendChild(video);
    video.load();

    const tex = new THREE.VideoTexture(video);
    const baseH = (VIDEO_WIDTH_M * 9) / 16;
    const panel = new THREE.Mesh(
      new THREE.PlaneGeometry(VIDEO_WIDTH_M, baseH),
      new THREE.MeshBasicMaterial({ map: tex, side: THREE.DoubleSide }),
    );
    panel.position.set(pos.x, pos.y + DISPLAY_HEIGHT_M, pos.z);
    // Resize to the real aspect once known (portrait videos would otherwise
    // render squashed); longest side capped at VIDEO_WIDTH_M.
    video.addEventListener('loadedmetadata', () => {
      const aspect = video.videoWidth / video.videoHeight || 16 / 9;
      const w = aspect >= 1 ? VIDEO_WIDTH_M : VIDEO_WIDTH_M * aspect;
      const h = aspect >= 1 ? VIDEO_WIDTH_M / aspect : VIDEO_WIDTH_M;
      panel.scale.set(w / VIDEO_WIDTH_M, h / baseH, 1);
    });

    // Mute/unmute button pinned to the panel's bottom-right corner (child of
    // the panel, so it billboards along with it).
    const muteBtn = new THREE.Mesh(
      new THREE.PlaneGeometry(0.14, 0.14),
      new THREE.MeshBasicMaterial({ map: makeIconTexture('🔇'), transparent: true }),
    );
    muteBtn.position.set(VIDEO_WIDTH_M / 2 - 0.1, -((VIDEO_WIDTH_M * 9) / 16) / 2 - 0.1, 0.01);
    panel.add(muteBtn);

    this.group.add(panel);
    this.billboards.push(panel);
    this.videos.push({ el: video, obj: panel, muteBtn, userPaused: false });
  }

  private async addModel(url: string, pos: { x: number; y: number; z: number }): Promise<void> {
    const loader = new GLTFLoader();
    const draco = new DRACOLoader();
    draco.setDecoderPath('/draco/');
    loader.setDRACOLoader(draco);
    try {
      const gltf = await loader.loadAsync(url);
      const obj = gltf.scene;
      const box = new THREE.Box3().setFromObject(obj);
      const size = box.getSize(new THREE.Vector3());
      const maxDim = Math.max(size.x, size.y, size.z) || 1;
      const scale = MODEL_MAX_DIM_M / maxDim;
      obj.scale.setScalar(scale);
      // Rest the model's bounding-box bottom on the POI's floor point.
      obj.position.set(pos.x, pos.y - box.min.y * scale, pos.z);
      this.group.add(obj);
    } finally {
      draco.dispose();
    }
  }

  /**
   * Audio narration: a floating 🔉 disc marks the spot; playback triggers on
   * approach (<4 m) and re-arms after leaving (>6 m). iOS may block the
   * automatic play() — tapping the disc always works (user gesture).
   */
  private addAudio(url: string, poiName: string, pos: { x: number; y: number; z: number }): void {
    const el = document.createElement('audio');
    el.crossOrigin = 'anonymous'; // BEFORE src (same CORS-taint rule as video)
    el.preload = 'auto';
    el.src = url;
    el.addEventListener('error', () =>
      logEvent('error', `audio element failed: ${el.error?.message ?? 'unknown'} (${poiName})`),
    );
    el.addEventListener('ended', () => this.setAudioIcon(el, false));

    const icon = new THREE.Mesh(
      new THREE.PlaneGeometry(0.22, 0.22),
      new THREE.MeshBasicMaterial({ map: makeIconTexture('🔉'), transparent: true }),
    );
    icon.position.set(pos.x, pos.y + DISPLAY_HEIGHT_M, pos.z);
    this.group.add(icon);
    this.billboards.push(icon);
    this.audios.push({ el, icon, name: poiName, inRange: false });
  }

  private setAudioIcon(el: HTMLAudioElement, playing: boolean): void {
    const entry = this.audios.find((a) => a.el === el);
    if (!entry) return;
    const mat = entry.icon.material as THREE.MeshBasicMaterial;
    mat.map = makeIconTexture(playing ? '🔊' : '🔉');
    mat.needsUpdate = true;
  }

  /** Floating "open webpage" button — tap (see handleTap) opens the URL. */
  private addLink(url: string, poiName: string, pos: { x: number; y: number; z: number }): void {
    // Stored mediaUrl is untrusted — never create a tap target for a
    // non-http(s) URL (javascript:/data: would execute in this origin).
    if (!isSafeHttpUrl(url)) {
      logEvent('error', `blocked unsafe link URL on POI "${poiName}"`);
      return;
    }
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 160;
    const ctx = canvas.getContext('2d')!;
    // Rounded pill
    ctx.fillStyle = 'rgba(15, 23, 42, 0.92)';
    ctx.strokeStyle = '#38bdf8';
    ctx.lineWidth = 8;
    roundRect(ctx, 8, 8, canvas.width - 16, canvas.height - 16, 40);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = '#e2e8f0';
    ctx.font = 'bold 52px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const label = poiName.length > 14 ? `${poiName.slice(0, 13)}…` : poiName;
    ctx.fillText(`🔗 ${label}`, canvas.width / 2, canvas.height / 2 - 14);
    ctx.font = '34px system-ui, sans-serif';
    ctx.fillStyle = '#38bdf8';
    ctx.fillText('tap to open', canvas.width / 2, canvas.height / 2 + 42);

    const tex = new THREE.CanvasTexture(canvas);
    const panel = new THREE.Mesh(
      new THREE.PlaneGeometry(0.8, 0.25),
      new THREE.MeshBasicMaterial({ map: tex, transparent: true, side: THREE.DoubleSide }),
    );
    panel.position.set(pos.x, pos.y + DISPLAY_HEIGHT_M, pos.z);
    this.group.add(panel);
    this.billboards.push(panel);
    this.links.push({ obj: panel, url, name: poiName });
  }

  /**
   * Screen tap → raycast against interactive media, nearest hit wins:
   * - mute button → toggle sound (user gesture, so unmuted play is allowed)
   * - video panel → toggle play/pause
   * - link button → open URL
   * Returns true if the tap was consumed.
   */
  handleTap(camera: THREE.Camera, ndcX: number, ndcY: number): boolean {
    const targets: THREE.Object3D[] = [
      ...this.videos.map((v) => v.muteBtn),
      ...this.videos.map((v) => v.obj),
      ...this.links.map((l) => l.obj),
      ...this.audios.map((a) => a.icon),
    ];
    if (targets.length === 0) return false;
    this.raycaster.setFromCamera(new THREE.Vector2(ndcX, ndcY), camera);
    const hits = this.raycaster.intersectObjects(targets, false);
    if (hits.length === 0) return false;
    const hit = hits[0].object;

    const byMute = this.videos.find((v) => v.muteBtn === hit);
    if (byMute) {
      byMute.el.muted = !byMute.el.muted;
      (byMute.muteBtn.material as THREE.MeshBasicMaterial).map = makeIconTexture(
        byMute.el.muted ? '🔇' : '🔊',
      );
      (byMute.muteBtn.material as THREE.MeshBasicMaterial).needsUpdate = true;
      // Unmuting inside a tap handler satisfies mobile autoplay-with-sound rules.
      if (!byMute.el.muted && byMute.el.paused && !byMute.userPaused) {
        void byMute.el.play().catch(() => undefined);
      }
      logEvent('session', `video ${byMute.el.muted ? 'muted' : 'unmuted'}`);
      return true;
    }

    const byPanel = this.videos.find((v) => v.obj === hit);
    if (byPanel) {
      byPanel.userPaused = !byPanel.userPaused;
      if (byPanel.userPaused) byPanel.el.pause();
      else void byPanel.el.play().catch(() => undefined);
      // Dim while paused so the state is visible.
      (byPanel.obj.material as THREE.MeshBasicMaterial).color.setScalar(
        byPanel.userPaused ? 0.45 : 1,
      );
      logEvent('session', `video ${byPanel.userPaused ? 'paused' : 'resumed'} by tap`);
      return true;
    }

    const byAudio = this.audios.find((a) => a.icon === hit);
    if (byAudio) {
      if (byAudio.el.paused) {
        void byAudio.el.play().then(() => this.setAudioIcon(byAudio.el, true)).catch(() => undefined);
        logEvent('session', `audio play (tap) — ${byAudio.name}`);
      } else {
        byAudio.el.pause();
        this.setAudioIcon(byAudio.el, false);
        logEvent('session', `audio pause (tap) — ${byAudio.name}`);
      }
      return true;
    }

    const link = this.links.find((l) => l.obj === hit);
    if (!link) return false;
    if (!isSafeHttpUrl(link.url)) return false; // belt-and-braces (addLink already filters)
    logEvent('session', `opened link ${link.url}`);
    if (this.onOpenLink) this.onOpenLink(link.url, link.name);
    else window.open(link.url, '_blank', 'noopener');
    return true;
  }

  /** Per-frame: billboard panels toward the camera; gate video playback by distance. */
  update(camera: THREE.Camera): void {
    if (!this.poseTransform.isLocalized || this.group.children.length === 0) return;

    const camPos = new THREE.Vector3();
    camera.getWorldPosition(camPos);

    for (const b of this.billboards) {
      const p = new THREE.Vector3();
      b.getWorldPosition(p);
      // Yaw-only billboard: keep panels upright, face the viewer.
      b.lookAt(camPos.x, p.y, camPos.z);
    }

    for (const { el, obj, userPaused } of this.videos) {
      const p = new THREE.Vector3();
      obj.getWorldPosition(p);
      const near = p.distanceTo(camPos) < VIDEO_PLAY_DIST_M;
      // Distance gating never overrides an explicit user pause.
      if (near && el.paused && !userPaused) void el.play().catch(() => undefined);
      else if (!near && !el.paused) el.pause();
    }

    // Media impressions: first time within 8 m, once per POI per session.
    if (this.onImpression) {
      for (const target of this.impressionTargets) {
        if (this.impressed.has(target.poiId)) continue;
        const p = new THREE.Vector3();
        target.obj.getWorldPosition(p);
        if (p.distanceTo(camPos) < 8) {
          this.impressed.add(target.poiId);
          this.onImpression(target.poiId);
        }
      }
    }

    // Audio narration: trigger once on approach, re-arm after leaving.
    for (const a of this.audios) {
      const p = new THREE.Vector3();
      a.icon.getWorldPosition(p);
      const d = p.distanceTo(camPos);
      if (!a.inRange && d < AUDIO_TRIGGER_DIST_M) {
        a.inRange = true;
        a.el.currentTime = 0;
        void a.el
          .play()
          .then(() => {
            this.setAudioIcon(a.el, true);
            logEvent('session', `audio narration started — ${a.name}`);
          })
          .catch(() => logEvent('session', `audio autoplay blocked — tap 🔉 (${a.name})`));
      } else if (a.inRange && d > AUDIO_RESET_DIST_M) {
        a.inRange = false;
        if (!a.el.paused) a.el.pause();
        this.setAudioIcon(a.el, false);
      }
    }
  }

  /** Stop all playback (session end). */
  pauseAll(): void {
    for (const { el } of this.videos) el.pause();
    for (const { el } of this.audios) el.pause();
  }
}

/** Small round icon texture (e.g. 🔇/🔊) for in-AR buttons. */
function makeIconTexture(emoji: string): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = 128;
  canvas.height = 128;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = 'rgba(15, 23, 42, 0.9)';
  ctx.beginPath();
  ctx.arc(64, 64, 60, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = '#38bdf8';
  ctx.lineWidth = 6;
  ctx.stroke();
  ctx.font = '64px system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(emoji, 64, 70);
  return new THREE.CanvasTexture(canvas);
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}
